import {
    makeObservable,
    override,
} from 'mobx';
import { RootStore } from './RootStore';
import { DataStore } from './DataStore';
import { IOrganizationAdminInvite } from './models/OrganizationAdminInvite';
import { IAdministratorOverview } from './models/interfaces/IAdministratorOverview';
import { AppError } from './models/errors/AppError';
import { AppErrors } from './models/constants/AppErrors';
import { IServerUser, ServerUser } from './models/server/ServerUser';

enum InvitationRequestTypes {
    PostAdminInvite = 'postAdminInvite',
    GetAdminInvite = 'getAdminInvite',
    RefreshInviteToken = 'refreshInviteToken',
    AcceptInvite = 'acceptInvite',
}

export class InvitationStore extends DataStore<InvitationRequestTypes> {

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this);
    }

    /********* API Request Methods *********/

    getAdminInvite = async (token: string) => {
        try {
            const response = await this.makeRequest<{ organization: { id: number, name: string }, email: string, logoutRequired: boolean, inviteForCurrentUser: boolean | undefined }>(
                InvitationRequestTypes.GetAdminInvite,
                undefined,
                { token },
                true
            );
            if (!response) throw new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            return response;
        } catch (err) {
            if (err instanceof AppError) {
                return err;
            } else {
                return new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            }
        }
    };

    postAdminInvite = async (invite: IOrganizationAdminInvite) => {
        try {
            const response = await this.makeRequest<IAdministratorOverview>(
                InvitationRequestTypes.PostAdminInvite,
                { email: invite.email, organizationId: this.rootStore.userStore.user.organization?.id },
                undefined,
                true
            );
            if (!response) throw new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            return response;
        } catch (err) {
            if (err instanceof AppError) {
                return err;
            } else {
                return new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            }
        }
    };

    refreshAdminInvite = async (token: string) => {
        try {
            const response = await this.makeRequest<{ success: boolean }>(
                InvitationRequestTypes.RefreshInviteToken,
                undefined,
                { token },
                true
            );
            if (!response) throw new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            return response;
        } catch (err) {
            if (err instanceof AppError) {
                return err;
            } else {
                return new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            }
        }
    };

    acceptAdminInvite = async (token: string, termsOfServiceAccepted: boolean, password?: string) => {
        try {
            const response = await this.makeRequest<{ user: IServerUser }>(
                InvitationRequestTypes.AcceptInvite,
                {
                    termsOfServiceAccepted,
                    password
                },
                { token },
                true
            );
            if (!response) throw new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            if (response.user) {
                const user = new ServerUser(response.user).deserialize();
                this.rootStore.userStore.setAuthenticatedUser(user);
            }
            return response;
        } catch (err) {
            if (err instanceof AppError) {
                throw err;
            } else {
                throw new AppError(AppErrors.INTERNAL_SERVER_ERROR);
            }
        }
    };

    /****** Computed Data ******/

    @override get getRequests() {
        return [InvitationRequestTypes.GetAdminInvite];
    }

    @override get putRequests() {
        return [InvitationRequestTypes.RefreshInviteToken, InvitationRequestTypes.AcceptInvite];
    }

    @override get postRequests() {
        return [InvitationRequestTypes.PostAdminInvite];
    }

    @override get requestURLs() {

        const invitationsApi = '/api/invitations';

        return {
            [InvitationRequestTypes.GetAdminInvite]: (data: undefined, params: { token: string }) => `${invitationsApi}/${params.token}`,
            [InvitationRequestTypes.PostAdminInvite]: `${invitationsApi}`,
            [InvitationRequestTypes.RefreshInviteToken]: (data: undefined, params: { token: string }) => `${invitationsApi}/${params.token}/reset`,
            [InvitationRequestTypes.AcceptInvite]: (data: undefined, params: { token: string }) => `${invitationsApi}/${params.token}/accept`,
        };
    }
}